import { ColDef, ColGroupDef, HeaderClassParams, IServerSideDatasource, ValueFormatterParams } from "ag-grid-community";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import AgGridCustomTable from "../../../../../../../../../util/component/AgGridCustomTable";
import PinnedRowRender from "../../_components/pinned-row-render/PinnedRowRender";
import DashboardHeaderCol from "../../_components/dashboard-header-column/DashboardHeaderCol";
import { pendingBookingsBySegmentAndCategoryFilterState } from "../../../../../recoil";
import { DEFAULT_TIME, IFormValuesPendingBookingsBySegmentListDashboardFilter, IPendingBookingsBySegmentAndCategory, IPendingBookingsBySegmentAndCategoryList, ITotalPendingBookingsBySegmentAndCategory } from "../../../../../types";
import { ISortBy } from "../../../../../../../api/types";
import { ReactQueryKey } from "../../../../../../../types";
import { getTotalPendingBookingsBySegmentAndCategory, getPendingBookingsBySegmentAndCategory } from "../../../../../../../api/services";
import "../../../../../dashboard.css";
import CustomLoader from "../../../../../../../../../util/component/CustomLoader";

/**
 * Table component of pending bookings by segment and category
 */
const TablePendingBksBySeg = () => {

    const {t} = useTranslation('booking');

    const pendingListFilter = useRecoilValue(pendingBookingsBySegmentAndCategoryFilterState);

    const queryClient = useQueryClient();

    const [pinnedBottomRowData, setPinnedBottomRowData] = useState<ITotalPendingBookingsBySegmentAndCategory | undefined>(undefined);

    const pendingBookingsBySegmentAndCategoryListMutation = useMutation(getPendingBookingsBySegmentAndCategory);
    const pendingBookingsBySegmentAndCategorySummaryMutation = useMutation(getTotalPendingBookingsBySegmentAndCategory);

    // Each Column Definition results in one Column.
    const columnDefs = useMemo<ColDef<IPendingBookingsBySegmentAndCategory>[]>(() => [
        {
            field: 'segment',
            headerName: t('label.pendingBookingsBySegmentAndCategorySegmentColumn'),
            minWidth: 100,
            suppressColumnsToolPanel: true,
            rowGroup: true,
            lockPosition: "left",
            hide: true
        },
        {
            field: 'category',
            headerName: '',
            minWidth: 100,
            suppressColumnsToolPanel: true,
            lockPosition: "left",
            hide: true,
            resizable: true
        },
        {
            headerName: t('label.pendingBookingsByLocationTotalGroupColumn'),
            marryChildren: true,
            groupId: 'GroupTotal',
            children: [
                {
                    field: 'toBeWorked',
                    headerName: t('label.pendingBookingsBySegmentAndCategoryToBeWorkedColumn'),
                    suppressColumnsToolPanel: true,
                    headerTooltip: t("label.pendingBookingsBySegmentAndCategoryToBeWorkedColumnTooltip"),
                    flex: 1,
                    resizable: true
                },
                {
                    field: 'maxIdleTime',
                    headerName: t('label.pendingBookingsBySegmentAndCategoryMaxIdleTimeColumn'),
                    suppressColumnsToolPanel: true,
                    flex: 1,
                    minWidth: 120,
                    resizable: true,
                    valueFormatter: (params: ValueFormatterParams<IPendingBookingsBySegmentAndCategory>) => {
                        return !!params.data?.maxIdleTime  ? params.data?.maxIdleTime : DEFAULT_TIME;
                    }
                },
            ]
        },
        {
            headerName: t('label.pendingBookingsByLocationTodayGroupColumn'),
            marryChildren: true,
            groupId: 'GroupToday',
            children: [
                {
                    field: 'confirmed',
                    headerName: t('label.pendingBookingsBySegmentAndCategoryConfirmedColumn'),
                    suppressColumnsToolPanel: true,
                    flex: 1,
                    minWidth: 125,
                    resizable: true
                },
                {
                    field: 'rejectedUnavailable',
                    headerName: t('label.pendingBookingsBySegmentAndCategoryRejectedUnavailableColumn'),
                    suppressColumnsToolPanel: true,
                    flex: 1,
                    resizable: true
                },
                {
                    field: 'worked',
                    headerName: t('label.pendingBookingsBySegmentAndCategoryWorkedColumn'),
                    suppressColumnsToolPanel: true,
                    headerTooltip: t("label.pendingBookingsBySegmentAndCategoryWorkedColumnTooltip"),
                    flex: 1,
                    width: 130,
                    resizable: true
                },
                {
                    field: 'avgWorkingTime',
                    headerName: t('label.pendingBookingsBySegmentAndCategoryAvgWorkingTimeColumn'),
                    suppressColumnsToolPanel: true,
                    headerTooltip: t("label.pendingBookingsBySegmentAndCategoryAvgWorkingTimeColumnTooltip"),
                    flex: 1,
                    resizable: true,
                    valueFormatter: (params: ValueFormatterParams<IPendingBookingsBySegmentAndCategory>) => {
                        return !!params.data?.avgWorkingTime  ? params.data?.avgWorkingTime : DEFAULT_TIME;
                    }
                },
                {
                    field: 'maxWorkingTime',
                    headerName: t('label.pendingBookingsBySegmentAndCategoryMaxWorkingTimeColumn'),
                    suppressColumnsToolPanel: true,
                    headerTooltip: t("label.pendingBookingsBySegmentAndCategoryMaxWorkingTimeColumnTooltip"),
                    flex: 1,
                    resizable: true,
                    valueFormatter: (params: ValueFormatterParams<IPendingBookingsBySegmentAndCategory>) => {
                        return !!params.data?.maxWorkingTime  ? params.data?.maxWorkingTime : DEFAULT_TIME;
                    }
                }]
        }
    ], [t]);

    // defaultColGroupDef sets props common to all Goup Columns
    const defaultColGroupDef = useMemo<Partial<ColGroupDef>>(() => {
        return { headerClass: headerClassFunc };
    }, [columnDefs]);

    // "auto column" is included as the first column in the grid
    const autoGroupColumnDef = useMemo<ColDef>(() => ({
            headerName: t('label.pendingBookingsBySegmentAndCategorySegmentColumn'),
            minWidth: 100,
            field: 'category',
            resizable: true,
            lockPosition: "left"
    }), []);

    // DefaultColDef sets props common to all Columns
    const defaultColDef = useMemo<ColDef>(() => ({
        resizable: false,
        unSortIcon: true,
        minWidth: 130,
        wrapHeaderText: true,
        autoHeaderHeight: true,
        headerComponent: DashboardHeaderCol,
        cellRendererSelector: (params: any) => {
            if (params.node.rowPinned) {
                return {
                    component: PinnedRowRender,
                    params: {
                        field: ['segment', 'category'],
                        style: { 'fontWeight': 'bold' },
                    },
                };
            } else {
                // rows that are not pinned don't use any cell renderer
                return undefined;
            }
        }
    }), []);

    // set class for Group Column
    function headerClassFunc(params: HeaderClassParams) {
        let cssClass = 'dashboard-column-group-no-view-resize'
        const colDef = params?.columnGroup?.getDefinition() as ColGroupDef;
        if (colDef?.groupId === 'GroupTotal' || colDef?.groupId === 'GroupToday') {
            cssClass += ' dashboard-column-group';
        }
        return cssClass;
    }

    // load table record
    const serverSideDatasource = useMemo<IServerSideDatasource>(() => ({
        getRows: async (params) => {
            try {
                let pendingBookingsBySegmentAndCategorySummary: ITotalPendingBookingsBySegmentAndCategory = {} as ITotalPendingBookingsBySegmentAndCategory;
                let rowData: IPendingBookingsBySegmentAndCategory[] = [];
                const groupKeys = params.request.groupKeys;

                const start = !!params.request.startRow ? params.request.startRow : 0;
                const limit = (params.request.endRow !== undefined && params.request.startRow !== undefined) ? (params.request.endRow - params.request.startRow) : undefined;

                // data segment group by list - if not groupKeys
                if (groupKeys.length === 0) {
                    const sortBy: ISortBy | undefined = params.request.sortModel[0] ? {
                        field: params.request.sortModel[0].colId,
                        order: params.request.sortModel[0].sort
                    } : undefined;

                    // get total for header
                    queryClient.invalidateQueries({
                        queryKey: [ReactQueryKey.TotalHeaderDashboard]
                    });

                    // count for pagination and totals pinned row
                    pendingBookingsBySegmentAndCategorySummaryMutation.mutateAsync({
                        filters: pendingListFilter as IFormValuesPendingBookingsBySegmentListDashboardFilter
                    }).then((response: ITotalPendingBookingsBySegmentAndCategory) => {
                        //  set total for pinned row
                        pendingBookingsBySegmentAndCategorySummary = response;
                        if(!pendingBookingsBySegmentAndCategorySummary.avgWorkingTime) {
                            pendingBookingsBySegmentAndCategorySummary.avgWorkingTime = DEFAULT_TIME;
                        }
                        if(!pendingBookingsBySegmentAndCategorySummary.maxWorkingTime) {
                            pendingBookingsBySegmentAndCategorySummary.maxWorkingTime = DEFAULT_TIME;
                        }
                        //  set total for pinned row
                        setPinnedBottomRowData(pendingBookingsBySegmentAndCategorySummary);

                        // get records
                        pendingBookingsBySegmentAndCategoryListMutation.mutateAsync({
                            filters: pendingListFilter as IFormValuesPendingBookingsBySegmentListDashboardFilter,
                            sortBy: sortBy,
                            start: start,
                            limit: limit
                        }).then((response: IPendingBookingsBySegmentAndCategoryList[]) => {
                            const rowData = response
                                .map(elem => {
                                    elem.root['children'] = elem.children;
                                    return elem.root
                                });
                            params.success({
                                rowData: rowData,
                                rowCount: pendingBookingsBySegmentAndCategorySummary.totalRecords ?? 0
                            });

                            pendingBookingsBySegmentAndCategorySummary.totalRecords == 0 && params.api.showNoRowsOverlay();
                        })
                            .catch((err: AxiosError) => {
                                params.fail();
                                params.api.hideOverlay();
                            });
                    })
                    .catch((err: AxiosError) => {
                        params.fail();
                        params.api.hideOverlay();
                    });
                }

                // data city list
                if (groupKeys.length === 1 && groupKeys[0]) {
                    rowData = params.parentNode.data.children;

                    params.success({
                        // @ts-ignore
                        rowData: rowData.slice(start,start + limit),
                        rowCount: rowData.length
                    });
                }

                pendingBookingsBySegmentAndCategorySummary.totalRecords === 0 && params.api.showNoRowsOverlay();
            }
            catch {
                params.fail();
                params.api.hideOverlay();
            }
        }
    }), [pendingListFilter]);

    return (
        <>
            <AgGridCustomTable
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                serverSideDatasource= {serverSideDatasource}
                paginationPageSize={25}
                rowModelType="serverSide"
                pinnedBottomRowData={[pinnedBottomRowData]}
                defaultColGroupDef={defaultColGroupDef}
                suppressMultiSort
                suppressDragLeaveHidesColumns
                cacheBlockSize={25}
                enableCellTextSelection
                animateRows={true}
                autoGroupColumnDef={autoGroupColumnDef}
                tooltipShowDelay={0}
            />
            <CustomLoader
                loading={pendingBookingsBySegmentAndCategoryListMutation.isLoading ||
                pendingBookingsBySegmentAndCategorySummaryMutation.isLoading}
            />
        </>
    );
}

export default TablePendingBksBySeg;
